import { useStoreState } from 'easy-peasy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { getAnomalyTaskHistory, fetchCasesForAssetIdsAndStatus } from '@ge/feat-monitor/services';
import { QueryKey, TaskStatus, CaseStatus, IssueType, SortDirection } from '@ge/models/constants';
import { Config } from '@ge/shared/data-hooks';
import { useTableFilter } from '@ge/shared/data-hooks/use-table-filter';
import {
  getAssetCaseHistory,
  getAssetTaskHistory,
  getTurbineEventHistory,
} from '@ge/shared/services/cases';
import { sorter } from '@ge/util/metric-sorter';

import { taskPage } from '../services/task-page';

const CaseStatusMap = {
  [IssueType.CLOSED_CASES]: CaseStatus.CLOSED,
};
const queryConfig = {
  ...Config.EXECUTE_ONCE,
  refetchOnMount: true,
  refetchOnReconnect: true,
};

export const useAssetCaseHistory = ({ assetId, days, sortMetric, sortDirection, enabled }) => {
  const { data, error, isLoading, refetch, isRefetching } = useQuery(
    [QueryKey.CASE_NOTES, assetId, days],
    () => getAssetCaseHistory(assetId, days),
    {
      enabled: Boolean(enabled && assetId && days),
      refetchOnMount: true,
    },
  );

  return useMemo(() => {
    return {
      data: data?.length ? data.sort(sorter(sortMetric, sortDirection)) : [],
      error,
      isLoading,
      refetch,
      isRefetching,
    };
  }, [data, error, isLoading, sortDirection, sortMetric, refetch, isRefetching]);
};

export const useAssetTaskHistory = ({
  assetId,
  sortMetric,
  sortDirection,
  filters,
  search,
  enabled,
  taskUpdateForAsset,
}) => {
  const { t } = useTranslation(['tasks'], {
    useSuspense: false,
  });
  const { sites } = useStoreState((state) => state.sites);
  const { assets } = useStoreState((state) => state.assets);

  const siteId = assets[assetId]?.site?.id;
  const serviceGroupIds = sites[siteId]?.serviceGroups?.toString();

  const { data, error, isFetching } = useQuery(
    [QueryKey.ASSET_TASK_HISTORY, assetId, taskUpdateForAsset],
    async () => {
      let data = [];
      if (serviceGroupIds) {
        data = await getAssetTaskHistory([assetId], serviceGroupIds);
      } else {
        const response = await taskPage({ assetIds: [assetId], status: [TaskStatus.COMPLETED] });
        data = response?.tasks;
      }
      if (data?.length) {
        let results = data.map((task) => {
          task.techs = {
            assignedTechs: task?.assignedTechs,
            value: task?.assignedTechs?.map(
              (tech) => tech?.initials || tech?.firstName?.substring(0, 5),
            ),
          };
          task.source = t(`dynamic.types.${task.source}`, task.source);
          return task;
        });
        return results;
      }
    },
    {
      enabled: Boolean(enabled && assetId),
      ...queryConfig,
    },
  );

  const filteredData = useTableFilter({ data, filters, search });
  return useMemo(() => {
    return {
      data: filteredData.data?.length
        ? filteredData?.data?.sort(sorter(sortMetric, sortDirection))
        : [],
      filterValues: filteredData?.filterValues,
      error,
      isLoading: isFetching,
    };
  }, [filteredData, sortMetric, sortDirection, error, isFetching]);
};

//Turbine-event-history
export const useTurbineTaskHistory = ({
  assetId,
  sortMetric,
  sortDirection,
  filters,
  search,
  enabled,
  startDate,
  endDate,
}) => {
  const { data, error, isFetching } = useQuery(
    [QueryKey.TURBINE_EVENT_HISTORY, assetId, startDate, endDate],
    () => getTurbineEventHistory([assetId], startDate, endDate),
    {
      enabled: Boolean(enabled && assetId && startDate && endDate),
      refetchOnMount: true,
    },
  );
  let results = data?.events;
  let accumulatedEvents = [];
  results?.forEach((result) => {
    result?.eventHistory?.forEach((eventH) => {
      let mappingArr = {};
      mappingArr['code'] = result?.code;
      mappingArr['eventClass'] = result?.eventClass;
      mappingArr['desc'] = result?.desc;
      mappingArr['startDate'] = eventH?.startDate;
      mappingArr['endDate'] = eventH?.endDate;
      accumulatedEvents.push(mappingArr);
    });
  });
  const filteredEventsData = useTableFilter({ data: accumulatedEvents, filters, search });
  return useMemo(() => {
    return {
      data: filteredEventsData?.data?.length
        ? filteredEventsData?.data?.sort(sorter(sortMetric, sortDirection))
        : [],
      filterValues: filteredEventsData?.filterValues,
      isLoading: isFetching,
      error,
    };
  }, [sortMetric, sortDirection, filteredEventsData, isFetching, error]);
};
//Anomaly case history
export const useAnomalyCaseHistory = ({
  assetId = null,
  issueType = IssueType.CLOSED_CASES,
  includeChildCases,
  sortMetric,
  sortDirection,
  filters,
  search,
  enabled,
  startDate,
  endDate,
  isAnomalyHistoryTable = true,
}) => {
  const {
    data: _queryData,
    error,
    isFetching,
  } = useQuery(
    [
      QueryKey.ANOMALY_CASE_HISTORY,
      assetId,
      CaseStatusMap[issueType],
      startDate,
      endDate,
      (isAnomalyHistoryTable = true),
    ],
    () =>
      getAnomalyTaskHistory(
        [assetId],
        CaseStatusMap[issueType],
        (includeChildCases = false),
        startDate,
        endDate,
        (isAnomalyHistoryTable = true),
      ),
    {
      enabled: Boolean(enabled && assetId && startDate && endDate),
      refetchOnMount: true,
    },
  );
  let results = _queryData?.data;
  let camelCasePriority;
  let accumulatedDatas = [];
  results?.forEach((result) => {
    let priority =
      result?.priority.slice(0, 1).toUpperCase() + result?.priority.slice(1).toLowerCase();
    camelCasePriority = { ...result, priority };
    accumulatedDatas.push(camelCasePriority);
  });

  const filteredEventsData = useTableFilter({ data: accumulatedDatas, filters, search });
  const filtersOptions = filteredEventsData?.filterValues;
  let modifiedFilters;
  for (const property in filtersOptions) {
    modifiedFilters = {
      priority: ["Immediate", "High", "Medium", "Low"],
      description:filtersOptions[property],
    }
  }
  return useMemo(() => {
    return {
      data: filteredEventsData?.data?.length
        ? filteredEventsData?.data.sort(sorter(sortMetric, sortDirection))
        : [],
      filterValues: modifiedFilters ? modifiedFilters : filtersOptions,
      isLoading: isFetching,
      error,
    };
  }, [sortMetric, sortDirection, filteredEventsData, isFetching, error]);
};
